import { useCallback, useEffect, useState } from "react";
import {
  getPiOrgSettings,
  patchPiOrgSettings,
  type PiOrgSettingsDto,
} from "@/lib/piClient";

const EMPTY: PiOrgSettingsDto = {
  organization_id: "",
  enabled: false,
  cadence: "none",
  due_day_of_month: "last",
  grace_days: 0,
  notify_days_before: 3,
  assignee_mode: "location_members",
  photos_required: 2,
  photo_slot_1_label: "Building exterior in lot context",
  photo_slot_2_label: "Serial number",
  photo_slot_3_label: "",
  require_serial_readable: false,
  created_at: "",
  updated_at: "",
};

export function useOpsPhysicalInventorySettings(organizationId: string | null) {
  const [settings, setSettings] = useState<PiOrgSettingsDto>(EMPTY);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (!organizationId) return;
    setLoading(true);
    setError(null);
    const r = await getPiOrgSettings();
    setLoading(false);
    if (!r.ok) {
      setError(r.error);
      return;
    }
    setSettings(r.data.settings);
  }, [organizationId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const updateField = <K extends keyof PiOrgSettingsDto>(key: K, value: PiOrgSettingsDto[K]) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
    setSavedAt(null);
  };

  const save = useCallback(async () => {
    if (!organizationId) return;
    setSaving(true);
    setError(null);
    const r = await patchPiOrgSettings({
      enabled: settings.enabled,
      cadence: settings.cadence,
      due_day_of_month: settings.due_day_of_month,
      grace_days: settings.grace_days,
      notify_days_before: settings.notify_days_before,
      assignee_mode: settings.assignee_mode,
      photos_required: settings.photos_required,
      photo_slot_1_label: settings.photo_slot_1_label,
      photo_slot_2_label: settings.photo_slot_2_label,
      photo_slot_3_label: settings.photo_slot_3_label,
      require_serial_readable: settings.require_serial_readable,
    });
    setSaving(false);
    if (!r.ok) {
      setError(r.error);
      return;
    }
    setSettings(r.data.settings);
    setSavedAt(new Date().toISOString());
  }, [organizationId, settings]);

  return { settings, loading, saving, error, savedAt, updateField, save, reload };
}
